import type { AudioFrame } from "@/utils/arcade/beat";
import type { ArcadeTheme } from "@/utils/arcade/theme";
import type { ArtKit } from "./render";

export interface PointerTap {
  x: number;
  y: number;
  // A finger that went down while another was already on the screen.
  secondary?: boolean;
}

// Everything a game hears from the player during one frame. Coordinates are in
// CSS pixels inside the stage.
export interface FrameInput {
  taps: PointerTap[];
  pointer: { x: number; y: number; down: boolean; moved: boolean } | null;
  pressed: ReadonlySet<string>;
  held: ReadonlySet<string>;
}

export interface GameEnv {
  width: number;
  height: number;
  theme: ArcadeTheme;
  art: ArtKit;
  reducedMotion: boolean;
  difficulty: number;
  keyHints: boolean;
  rng: () => number;
  // Thumbnails: no score popups, banners or dashboard text in the frame.
  quiet?: boolean;
  controls?: { timingOffsetMs: number; largeTargets: boolean };
}

export interface GameStatus {
  score: number;
  lives: number;
  maxLives: number;
  combo: number;
  meter: number | null;
  over: boolean;
  level?: number;
  fever?: boolean;
}

// Moments the page turns into vibration (and could turn into sound). Engines
// queue them in update() and the stage drains them once per frame.
export type GameFeedback = "tap" | "score" | "hurt" | "crash" | "level" | "boss" | "win";

export function createFeedbackQueue() {
  let queue: GameFeedback[] = [];
  return {
    push(kind: GameFeedback) {
      if (queue.length < 8) queue.push(kind);
    },
    drain(): GameFeedback[] {
      const out = queue;
      queue = [];
      return out;
    },
  };
}

// What a game has going on right now, for the playtest suite. `threats` are
// things on screen the player has to dodge, hit or shoot; `pickups` are worth
// grabbing. `intensity` must climb as the game gets harder.
export interface GameProbe {
  threats: number;
  pickups: number;
  spawned: Record<string, number>;
  damage: number;
  intensity: number;
  boss: boolean;
  mission: string | null;
}

export function createTally() {
  const spawned: Record<string, number> = {};
  return {
    spawned,
    add(kind: string, count = 1) {
      spawned[kind] = (spawned[kind] ?? 0) + count;
    },
  };
}

// Counts each object once the first time a probe sees it, for engines that
// keep their entities in plain arrays.
export function createSpawnWatcher() {
  const seen = new WeakSet<object>();
  const tally = createTally();
  return {
    spawned: tally.spawned,
    see(kind: string, item: object) {
      if (seen.has(item)) return;
      seen.add(item);
      tally.add(kind);
    },
  };
}

export interface GameEngine {
  update(dt: number, input: FrameInput, audio: AudioFrame): void;
  draw(ctx: CanvasRenderingContext2D, audio: AudioFrame): void;
  resize(width: number, height: number): void;
  status(): GameStatus;
  // A bot that plays well enough to look good. It drives the attract mode
  // behind the Start screen, the card thumbnails and recorded clips.
  autopilot(audio: AudioFrame): FrameInput;
  // How much bloom the stage adds on top of this game's frame.
  readonly bloom: number;
  probe(): GameProbe;
  // Feedback since the last call, oldest first. The stage vibrates on these.
  feedback(): GameFeedback[];
  // Releases anything heavy the engine holds, such as a WebGL renderer.
  dispose?(): void;
  // Endless mode: restyle the running game for the next song.
  upgrade?(kind: "shield" | "heart" | "jump"): void;
  protect?(seconds: number): void;
  setTheme?(theme: ArcadeTheme, songTitle: string): void;
}

export const ACTION_KEYS = new Set(["Space", "ArrowUp", "KeyW", "Enter"]);

export const pressedAction = (input: FrameInput) => input.taps.length > 0 || [...input.pressed].some((code) => ACTION_KEYS.has(code));

export const EMPTY_INPUT: FrameInput = { taps: [], pointer: null, pressed: new Set(), held: new Set() };

export const tapInput = (x: number, y: number): FrameInput => ({ taps: [{ x, y }], pointer: { x, y, down: true, moved: true }, pressed: new Set(), held: new Set() });

export const pointerInput = (x: number, y: number): FrameInput => ({ taps: [], pointer: { x, y, down: true, moved: true }, pressed: new Set(), held: new Set() });
